<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import IdentityVerificationsAPI from 'dashboard/api/identityVerifications';
import LaisChecklistPanel from 'dashboard/components-next/LaisChecklistPanel.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisRedactedField from 'dashboard/components-next/LaisRedactedField.vue';

const STATUS_LABELS = {
  pending: 'Pendente',
  provider_pending: 'Provider',
  manual_review: 'Revisao manual',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  expired: 'Expirado',
  error: 'Erro tecnico',
};
const STATUS_CLASSES = {
  pending: 'bg-[#F2F4F7] text-[#5F6673]',
  provider_pending: 'bg-[#E8F1FF] text-[#175CD3]',
  manual_review: 'bg-[#FFF4D8] text-[#8A5A00]',
  approved: 'bg-[#EAF6F1] text-[#176B55]',
  rejected: 'bg-[#FDEBEC] text-[#B42318]',
  expired: 'bg-[#F2F4F7] text-[#5F6673]',
  error: 'bg-[#FDEBEC] text-[#B42318]',
};
const READINESS_LABELS = {
  ready: 'Pronto local',
  blocked: 'Fail-closed',
  not_configured: 'Nao configurado',
};
const READINESS_CLASSES = {
  ready: 'bg-[#EAF6F1] text-[#176B55]',
  blocked: 'bg-[#FDEBEC] text-[#B42318]',
  not_configured: 'bg-[#FFF4D8] text-[#8A5A00]',
};
const REVIEW_REASONS = [
  'identity_confirmed',
  'document_match',
  'provider_inconclusive',
  'insufficient_evidence',
  'fraud_suspected',
  'credit_policy_exception',
  'manual_waiver',
  'technical_error',
];

const filters = reactive({ status: '', provider: '', q: '' });
const uploadForm = reactive({
  contact_id: '',
  property_offer_id: '',
  provider: 'acessobio',
  party_role: 'buyer',
  purpose: 'manual_review',
  document_type: 'CNH',
  consent_accepted: false,
  metadata_source: 'dashboard_review_queue',
  selfie: null,
  document_front: null,
  document_back: null,
});
const reviewForm = reactive({
  decision: 'manual_review',
  decision_kind: 'identity',
  decision_reason: 'provider_inconclusive',
  double_check_user_id: '',
});
const creditForm = reactive({
  provider: 'acessobio',
  rule_version: 'credit/provider-result-v1',
});

const identities = ref([]);
const meta = ref({ count: 0, current_page: 1 });
const capabilityError = ref(null);
const currentAccount = useMapGetter('getCurrentAccount');
// FE-AUD-107: gate negativo de permissao antes de qualquer request sensivel de
// KYC/documentos. Enquanto a capability canonica de clientes estiver disponivel
// a fila carrega normalmente; um 403 do backend marca o contexto como negado e
// bloqueia novas cargas ao inves de depender do servidor rejeitar em loop.
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.LEADS_INDEX,
    account: currentAccount.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const canReviewKyc = computed(
  () => capabilitiesContext.value.actions.view?.visible !== false
);
const selectedId = ref(null);
const selectedIdentity = ref(null);
const providerReadiness = ref([]);
const signedUrls = ref({});
const isLoading = ref(false);
const isMutating = ref(false);

const selectedDocuments = computed(
  () => selectedIdentity.value?.documents || []
);
const selectedAttempts = computed(() => selectedIdentity.value?.attempts || []);
const selectedRiskDecisions = computed(
  () => selectedIdentity.value?.risk_decisions || []
);
const selectedCreditAssessments = computed(
  () => selectedIdentity.value?.credit_assessments || []
);
const readinessByProvider = computed(() =>
  Object.fromEntries(
    providerReadiness.value.map(readiness => [readiness.provider, readiness])
  )
);
const selectedProviderReadiness = computed(() => {
  const provider =
    selectedIdentity.value?.provider || filters.provider || uploadForm.provider;
  return readinessByProvider.value[provider] || null;
});
const readyProviderCount = computed(
  () =>
    providerReadiness.value.filter(readiness => readiness.can_dispatch).length
);
const checklistItems = computed(() => [
  {
    id: 'documents',
    label: 'Documentos sensiveis',
    status: selectedDocuments.value.length ? 'done' : 'blocked',
    evidenceRedacted: true,
    evidenceLabel: `${selectedDocuments.value.length} artefatos`,
  },
  {
    id: 'provider',
    label: 'Provider configurado',
    status:
      selectedProviderReadiness.value?.can_dispatch === false ||
      selectedIdentity.value?.latest_attempt?.status === 'failed'
        ? 'blocked'
        : 'pending',
    blocker:
      selectedProviderReadiness.value?.blocking_reasons?.[0] ||
      selectedIdentity.value?.latest_attempt?.error_code ||
      '',
  },
  {
    id: 'credit',
    label: 'Credito separado',
    status: selectedCreditAssessments.value.length ? 'pending' : 'na',
    evidenceRedacted: true,
  },
  {
    id: 'decision',
    label: 'Decisao com dupla checagem',
    status:
      selectedIdentity.value?.status === 'manual_review' ? 'pending' : 'done',
    deadline: selectedIdentity.value?.review_due_at,
  },
]);

const statusLabel = status => STATUS_LABELS[status] || status || 'Sem status';
const statusClass = status =>
  STATUS_CLASSES[status] || 'bg-[#F2F4F7] text-[#5F6673]';
const readinessLabel = status =>
  READINESS_LABELS[status] || status || 'Sem readiness';
const readinessClass = status =>
  READINESS_CLASSES[status] || 'bg-[#F2F4F7] text-[#5F6673]';
const capabilityLabel = (capability, readiness) =>
  `${capability}: ${readiness.ready ? 'ok' : 'bloqueado'}`;
const dateTime = value => {
  if (!value) return 'Sem data';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Data invalida';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const loadIdentity = async id => {
  if (!id || !canReviewKyc.value) return;
  selectedId.value = id;
  signedUrls.value = {};
  const { data } = await IdentityVerificationsAPI.show(id);
  selectedIdentity.value = data.data;
};

const loadQueue = async () => {
  if (!canReviewKyc.value) {
    capabilityError.value = { status: 403 };
    identities.value = [];
    selectedIdentity.value = null;
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await IdentityVerificationsAPI.reviewQueue({ ...filters });
    identities.value = data.data || [];
    meta.value = data.meta || {
      count: identities.value.length,
      current_page: 1,
    };
    selectedId.value =
      identities.value.find(identity => identity.id === selectedId.value)?.id ||
      identities.value[0]?.id ||
      null;
    capabilityError.value = null;
    if (selectedId.value) await loadIdentity(selectedId.value);
  } catch (error) {
    if (error?.response?.status === 403) capabilityError.value = error;
    useAlert('Nao foi possivel carregar a fila de KYC.');
  } finally {
    isLoading.value = false;
  }
};

const loadProviderReadiness = async () => {
  if (!canReviewKyc.value) return;
  try {
    const { data } = await IdentityVerificationsAPI.providerReadiness();
    providerReadiness.value = data.data || [];
  } catch (error) {
    if (error?.response?.status === 403) capabilityError.value = error;
    useAlert('Nao foi possivel carregar readiness dos providers KYC.');
  }
};

const updateFile = (key, event) => {
  uploadForm[key] = event.target.files?.[0] || null;
};

const submitUpload = async () => {
  isMutating.value = true;
  try {
    const payload = {
      ...uploadForm,
      metadata: { source: uploadForm.metadata_source },
    };
    const { data } =
      await IdentityVerificationsAPI.createWithDocuments(payload);
    useAlert('Documentos KYC registrados para revisao.');
    await loadQueue();
    await loadIdentity(data.data.id);
  } catch {
    useAlert('Nao foi possivel registrar os documentos KYC.');
  } finally {
    isMutating.value = false;
  }
};

const submitReview = async () => {
  if (!selectedIdentity.value) return;
  isMutating.value = true;
  try {
    await IdentityVerificationsAPI.review(selectedIdentity.value.id, {
      ...reviewForm,
      evidence_artifact_ids: selectedDocuments.value.map(
        document => document.id
      ),
      metadata: { source: 'dashboard_review_queue' },
    });
    useAlert('Decisao de risco salva.');
    await loadIdentity(selectedIdentity.value.id);
    await loadQueue();
  } catch {
    useAlert('Nao foi possivel salvar a decisao de risco.');
  } finally {
    isMutating.value = false;
  }
};

const requestCredit = async () => {
  if (!selectedIdentity.value) return;
  isMutating.value = true;
  try {
    await IdentityVerificationsAPI.requestCreditAssessment(
      selectedIdentity.value.id,
      {
        provider: creditForm.provider,
        criteria: { rule_version: creditForm.rule_version },
        metadata: { source: 'dashboard_review_queue' },
      }
    );
    useAlert('Avaliacao de credito enviada para revisao.');
    await loadIdentity(selectedIdentity.value.id);
  } catch {
    useAlert('Provider de credito indisponivel; item segue em revisao manual.');
  } finally {
    isMutating.value = false;
  }
};

const revealDocument = async document => {
  if (!canReviewKyc.value) return;
  if (!document.can_reveal || signedUrls.value[document.id]) return;
  const { data } = await IdentityVerificationsAPI.signedUrl(document.id, {
    expires_in: 120,
  });
  signedUrls.value = { ...signedUrls.value, [document.id]: data.data.url };
};

onMounted(async () => {
  if (!canReviewKyc.value) {
    capabilityError.value = { status: 403 };
    return;
  }
  await Promise.all([loadProviderReadiness(), loadQueue()]);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="min-h-screen bg-lais-bg-page">
    <div v-if="!canReviewKyc" class="px-4 py-8">
      <LaisPermissionState variant="denied" />
    </div>
    <template v-else>
      <LaisHeader
        title="KYC, credito e risco"
        subtitle="Fila operacional P36 com documentos redigidos por padrao"
      >
        <template #actions>
          <LaisPrimaryButton
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            @click="loadQueue"
          >
            Atualizar
          </LaisPrimaryButton>
        </template>
      </LaisHeader>

      <div class="grid gap-4 px-4 pb-8 lg:grid-cols-[360px_minmax(0,1fr)]">
        <section class="flex flex-col gap-3">
          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-3"
          >
            <div class="grid gap-2">
              <input
                v-model="filters.q"
                class="h-10 rounded-lais-sm border border-lais-border-soft bg-white px-3 text-lais-body-sm"
                placeholder="Buscar contato ou protocolo"
                @keyup.enter="loadQueue"
              />
              <div class="grid grid-cols-2 gap-2">
                <select
                  v-model="filters.status"
                  class="h-10 rounded-lais-sm border border-lais-border-soft bg-white px-3 text-lais-body-sm"
                >
                  <option value="">Status</option>
                  <option value="manual_review">Revisao manual</option>
                  <option value="provider_pending">Provider</option>
                  <option value="error">Erro</option>
                </select>
                <select
                  v-model="filters.provider"
                  class="h-10 rounded-lais-sm border border-lais-border-soft bg-white px-3 text-lais-body-sm"
                >
                  <option value="">Provider</option>
                  <option value="acessobio">AcessoBio</option>
                  <option value="manual">Manual</option>
                  <option value="zapsign">ZapSign</option>
                </select>
              </div>
              <button
                type="button"
                class="inline-flex h-9 items-center justify-center gap-2 rounded-lais-sm border border-lais-border-soft text-lais-label-sm font-semibold"
                @click="loadQueue"
              >
                <span class="i-lucide-list-filter size-4" aria-hidden="true" />
                Filtrar fila
              </button>
            </div>
          </div>

          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-3"
          >
            <header class="flex items-center justify-between gap-2">
              <p class="text-lais-label-sm font-semibold text-lais-text">
                Provider readiness
              </p>
              <span class="text-lais-label-xs text-lais-text-helper">
                {{ readyProviderCount }}/{{ providerReadiness.length }} dispatch
              </span>
            </header>
            <div class="mt-3 grid gap-2">
              <div
                v-for="readiness in providerReadiness"
                :key="readiness.provider"
                class="rounded-lais-sm border border-lais-border-soft p-2"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p
                      class="truncate text-lais-body-sm font-semibold text-lais-text"
                    >
                      {{ readiness.provider }}
                    </p>
                    <p class="text-lais-body-xs text-lais-text-helper">
                      Adapter {{ readiness.registration?.adapter || 'ausente' }}
                    </p>
                  </div>
                  <span
                    class="shrink-0 rounded-full px-2 py-0.5 text-lais-label-xs font-semibold"
                    :class="readinessClass(readiness.status)"
                  >
                    {{ readinessLabel(readiness.status) }}
                  </span>
                </div>
                <p class="mt-2 text-lais-body-xs text-lais-text-helper">
                  Rollback {{ readiness.rollback?.mode }} · segredo
                  {{
                    readiness.registration?.webhook_secret_configured
                      ? 'ok'
                      : 'ausente'
                  }}
                </p>
                <div class="mt-2 flex flex-wrap gap-1">
                  <span
                    v-for="[capability, capabilityReadiness] in Object.entries(
                      readiness.capabilities || {}
                    )"
                    :key="capability"
                    class="rounded-full bg-lais-bg-soft px-2 py-0.5 text-lais-label-xs text-lais-text-helper"
                  >
                    {{ capabilityLabel(capability, capabilityReadiness) }}
                  </span>
                </div>
                <p
                  v-if="readiness.blocking_reasons?.length"
                  class="mt-2 break-words text-lais-body-xs text-[#B42318]"
                >
                  {{ readiness.blocking_reasons.join(', ') }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-card"
          >
            <header class="border-b border-lais-border-soft px-3 py-2">
              <p class="text-lais-label-sm font-semibold text-lais-text">
                {{ meta.count }} revisoes
              </p>
            </header>
            <button
              v-for="identity in identities"
              :key="identity.id"
              type="button"
              class="flex w-full flex-col gap-2 border-b border-lais-border-soft px-3 py-3 text-left transition hover:bg-lais-bg-soft"
              :class="
                identity.id === selectedId ? 'bg-lais-bg-soft' : 'bg-white'
              "
              @click="loadIdentity(identity.id)"
            >
              <span class="flex items-center justify-between gap-2">
                <span
                  class="truncate text-lais-body-sm font-semibold text-lais-text"
                >
                  {{ identity.contact?.name || 'Contato sem nome' }}
                </span>
                <span
                  class="rounded-full px-2 py-0.5 text-lais-label-xs font-semibold"
                  :class="statusClass(identity.status)"
                >
                  {{ statusLabel(identity.status) }}
                </span>
              </span>
              <span class="text-lais-body-xs text-lais-text-helper">
                {{ identity.provider }} · {{ identity.party_role }} ·
                {{ dateTime(identity.updated_at) }}
              </span>
            </button>
          </div>
        </section>

        <section class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div class="flex min-w-0 flex-col gap-4">
            <section
              class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4"
            >
              <h2 class="text-lais-heading-sm font-semibold text-lais-text">
                Novo intake seguro
              </h2>
              <div class="mt-3 grid gap-3 md:grid-cols-2">
                <input
                  v-model="uploadForm.contact_id"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                  placeholder="contact_id"
                />
                <input
                  v-model="uploadForm.property_offer_id"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                  placeholder="property_offer_id opcional"
                />
                <select
                  v-model="uploadForm.provider"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                >
                  <option value="acessobio">AcessoBio</option>
                  <option value="manual">Manual</option>
                  <option value="zapsign">ZapSign</option>
                </select>
                <select
                  v-model="uploadForm.document_type"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                >
                  <option value="CNH">CNH</option>
                  <option value="RG">RG</option>
                  <option value="RNE">RNE</option>
                </select>
                <label
                  class="flex flex-col gap-1 text-lais-label-sm text-lais-text-helper"
                >
                  Selfie
                  <input
                    type="file"
                    accept="image/*"
                    @change="updateFile('selfie', $event)"
                  />
                </label>
                <label
                  class="flex flex-col gap-1 text-lais-label-sm text-lais-text-helper"
                >
                  Documento frente
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    @change="updateFile('document_front', $event)"
                  />
                </label>
                <label
                  class="flex flex-col gap-1 text-lais-label-sm text-lais-text-helper"
                >
                  Documento verso
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    @change="updateFile('document_back', $event)"
                  />
                </label>
                <label
                  class="flex items-center gap-2 text-lais-body-sm text-lais-text"
                >
                  <input
                    v-model="uploadForm.consent_accepted"
                    type="checkbox"
                  />
                  Consentimento LGPD coletado
                </label>
              </div>
              <button
                type="button"
                class="mt-3 inline-flex h-10 items-center gap-2 rounded-lais-sm bg-lais-primary px-3 text-lais-label-sm font-semibold text-white disabled:opacity-60"
                :disabled="isMutating"
                @click="submitUpload"
              >
                <span class="i-lucide-upload-cloud size-4" aria-hidden="true" />
                Registrar documentos
              </button>
            </section>

            <section
              v-if="selectedIdentity"
              class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4"
            >
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 class="text-lais-heading-sm font-semibold text-lais-text">
                    Revisao #{{ selectedIdentity.id }}
                  </h2>
                  <p class="text-lais-body-sm text-lais-text-helper">
                    {{ selectedIdentity.provider }} ·
                    {{ selectedIdentity.document_type }}
                  </p>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-lais-label-xs font-semibold"
                  :class="statusClass(selectedIdentity.status)"
                >
                  {{ statusLabel(selectedIdentity.status) }}
                </span>
              </div>

              <div class="mt-4 grid gap-3 md:grid-cols-2">
                <LaisRedactedField
                  label="Contato"
                  :value="selectedIdentity.contact?.name || ''"
                  :redacted="false"
                />
                <LaisRedactedField
                  label="Provider protocol"
                  :value="selectedIdentity.provider_protocol || ''"
                  :redacted="!selectedIdentity.provider_protocol"
                  reason="Sem protocolo aprovado pelo provider"
                />
              </div>

              <div class="mt-4">
                <LaisChecklistPanel
                  title="Checklist P36"
                  :items="checklistItems"
                  redacted
                />
              </div>

              <div class="mt-4 grid gap-3">
                <div
                  v-for="document in selectedDocuments"
                  :key="document.id"
                  class="flex flex-wrap items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft p-3"
                >
                  <div>
                    <p class="text-lais-body-sm font-semibold text-lais-text">
                      {{ document.artifact_kind }}
                    </p>
                    <p class="text-lais-body-xs text-lais-text-helper">
                      {{ document.storage_state }} ·
                      {{ document.metadata?.content_type || 'tipo protegido' }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-8 items-center gap-2 rounded-lais-sm border border-lais-border-soft px-2 text-lais-label-xs font-semibold disabled:opacity-50"
                    :disabled="!document.can_reveal"
                    @click="revealDocument(document)"
                  >
                    <span class="i-lucide-eye size-3.5" aria-hidden="true" />
                    Revelar com auditoria
                  </button>
                  <a
                    v-if="signedUrls[document.id]"
                    class="text-lais-label-xs font-semibold text-lais-primary"
                    :href="signedUrls[document.id]"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir URL curta
                  </a>
                </div>
              </div>
            </section>
          </div>

          <aside class="flex min-w-0 flex-col gap-4">
            <section
              v-if="selectedIdentity"
              class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4"
            >
              <h2 class="text-lais-heading-sm font-semibold text-lais-text">
                Decisao de risco
              </h2>
              <div class="mt-3 grid gap-2">
                <select
                  v-model="reviewForm.decision"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                >
                  <option value="manual_review">Manter em revisao</option>
                  <option value="approved">Aprovar</option>
                  <option value="rejected">Rejeitar</option>
                  <option value="error">Erro tecnico</option>
                </select>
                <select
                  v-model="reviewForm.decision_reason"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                >
                  <option
                    v-for="reason in REVIEW_REASONS"
                    :key="reason"
                    :value="reason"
                  >
                    {{ reason }}
                  </option>
                </select>
                <input
                  v-model="reviewForm.double_check_user_id"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                  placeholder="double_check_user_id para aprovar/rejeitar"
                />
                <button
                  type="button"
                  class="inline-flex h-10 items-center justify-center gap-2 rounded-lais-sm bg-lais-primary px-3 text-lais-label-sm font-semibold text-white disabled:opacity-60"
                  :disabled="isMutating"
                  @click="submitReview"
                >
                  <span
                    class="i-lucide-shield-check size-4"
                    aria-hidden="true"
                  />
                  Salvar decisao
                </button>
              </div>
            </section>

            <section
              v-if="selectedIdentity"
              class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4"
            >
              <h2 class="text-lais-heading-sm font-semibold text-lais-text">
                Credito
              </h2>
              <div class="mt-3 grid gap-2">
                <select
                  v-model="creditForm.provider"
                  class="h-10 rounded-lais-sm border border-lais-border-soft px-3 text-lais-body-sm"
                >
                  <option value="acessobio">AcessoBio</option>
                  <option value="manual">Manual</option>
                </select>
                <button
                  type="button"
                  class="inline-flex h-10 items-center justify-center gap-2 rounded-lais-sm border border-lais-border-soft px-3 text-lais-label-sm font-semibold"
                  :disabled="isMutating"
                  @click="requestCredit"
                >
                  <span class="i-lucide-gauge size-4" aria-hidden="true" />
                  Solicitar avaliacao
                </button>
              </div>
            </section>

            <section
              v-if="selectedIdentity"
              class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4"
            >
              <h2 class="text-lais-heading-sm font-semibold text-lais-text">
                Auditoria redigida
              </h2>
              <div class="mt-3 grid gap-3">
                <LaisPayloadViewer
                  title="Tentativas"
                  :payload="selectedAttempts"
                  :default-open="false"
                />
                <LaisPayloadViewer
                  title="Decisoes"
                  :payload="selectedRiskDecisions"
                  :default-open="false"
                />
              </div>
            </section>
          </aside>
        </section>
      </div>
    </template>
  </main>
</template>
